import {
	KYCAuthorizationOriginSourceEnum,
	KYCConfirmationStateEnum,
} from '.spec/self-care/tsTypes';
import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BnplProductType, MyProfileQueryData } from '@skippay/core/@types/myProfile';
import { useGoogleAnalytics } from '@skippay/core/components/GoogleAnalytics';
import { NOT_FOUND } from '@skippay/core/constants/httpStatusCodes';
import { useBackendErrorMessages } from '@skippay/core/hooks/useBackendErrorMessages';
import useLocation from '@skippay/core/hooks/useLocation';
import { useIntl } from '@skippay/core/i18n/intl';
import { spawnNotificationError } from '@skippay/core/modules/notifications';
import { firstAxiosError } from '@skippay/core/utils/gqlUtils';

import MyProfileQuery from 'queries/MyProfileQuery.graphql';

import { GA_ECAT, isTerminalSlug } from '../constants';
import BnplLimitsApplicationChangeMutation from '../mutations/BnplLimitsApplicationChangeMutation.graphql';
import BnplLimitsApplicationCreateMutation from '../mutations/BnplLimitsApplicationCreateMutation.graphql';
import BnplLimitsApplicationQuery from '../queries/BnplLimitsApplicationQuery.graphql';
import {
	BnplLimitSetupApplication,
	BnplLimitSetupChangeResult,
	BnplLimitSetupCreateResult,
	BnplLimitSetupQueryResult,
	BnplLimitSetupVariables,
} from '../types';

export interface UseBnplLimitManagement {
	loading: boolean;
	application: BnplLimitSetupApplication | null;
	isSubmitting: boolean;
	error: boolean;
	onConfirmProducts: (types: BnplProductType[]) => void;
	onConfirmKycData: () => void;
	onDeclineKycData: () => void;
}

export function useBnplLimitManagement(): UseBnplLimitManagement {
	const apolloClient = useApolloClient();
	const dispatch = useDispatch();
	const intl = useIntl();
	const { formatError, normalizeBackendErrors } = useBackendErrorMessages();
	const { sendEvent } = useGoogleAnalytics();
	const {
		location: { query: { source } = {} },
	} = useLocation();

	const [application, setApplication] = useState<BnplLimitSetupApplication | null>(null);
	const [loading, setLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(false);

	const bootstrapped = useRef(false);

	const notifyError = useCallback(
		(caughtError: any) => {
			dispatch(
				spawnNotificationError({
					id: 'bnplLimitsApplicationError',
					title:
						formatError(normalizeBackendErrors(caughtError)[0]) ||
						intl.formatMessage({
							id: 'BnplLimitManagement.error',
							defaultMessage: 'Omlouváme se, něco se nepovedlo. Zkuste to prosím později.',
						}),
				})
			);
		},
		[dispatch, formatError, normalizeBackendErrors, intl]
	);

	const getActiveApplication = useCallback(
		() =>
			apolloClient
				.query<BnplLimitSetupQueryResult>({
					query: BnplLimitsApplicationQuery,
					fetchPolicy: 'network-only',
				})
				.then(({ data }) => data.accountBnplLimitsApplication)
				.catch((caughtError) => {
					if (firstAxiosError(caughtError).status === NOT_FOUND) {
						return null;
					}
					throw caughtError;
				}),
		[apolloClient]
	);

	const createApplication = useCallback(
		() =>
			apolloClient
				.mutate<BnplLimitSetupCreateResult>({
					mutation: BnplLimitsApplicationCreateMutation,
				})
				.then(({ data }) => data?.accountBnplLimitsApplicationCreate ?? null),
		[apolloClient]
	);

	const canApplyForBnplLimits = useCallback(() => {
		const profile = apolloClient.readQuery<MyProfileQueryData>({ query: MyProfileQuery });
		return Boolean(profile?.myProfile.options.canApplyForBnplLimits);
	}, [apolloClient]);

	useEffect(() => {
		if (bootstrapped.current) return;
		bootstrapped.current = true;

		const isKycReturn = source === KYCAuthorizationOriginSourceEnum.BNPL_LIMITS_APPLICATION;

		(async () => {
			try {
				const active = await getActiveApplication();

				if (isKycReturn) {
					setApplication(active);
					return;
				}

				if (active && !isTerminalSlug(active.slug)) {
					setApplication(active);
					return;
				}

				if (canApplyForBnplLimits()) {
					setApplication(await createApplication());
				} else {
					setApplication(active);
				}
			} catch (caughtError) {
				setError(true);
				notifyError(caughtError);
			} finally {
				setLoading(false);
			}
		})();
	}, [source, getActiveApplication, createApplication, canApplyForBnplLimits, notifyError]);

	const change = useCallback(
		(body: BnplLimitSetupVariables['body'], event?: { eAct: string; eLab: string }) => {
			setError(false);
			setIsSubmitting(true);
			if (event) sendEvent({ eCat: GA_ECAT, ...event });

			return apolloClient
				.mutate<BnplLimitSetupChangeResult, BnplLimitSetupVariables>({
					mutation: BnplLimitsApplicationChangeMutation,
					variables: { body },
				})
				.then(({ data }) => {
					if (!data?.accountBnplLimitsApplicationChange) return;

					setApplication(data.accountBnplLimitsApplicationChange);

					if (isTerminalSlug(data.accountBnplLimitsApplicationChange.slug)) {
						apolloClient.query({ query: MyProfileQuery, fetchPolicy: 'network-only' });
					}
				})
				.catch((caughtError) => {
					setError(true);
					notifyError(caughtError);
				})
				.finally(() => setIsSubmitting(false));
		},
		[apolloClient, sendEvent, notifyError]
	);

	const onConfirmProducts = useCallback(
		(types: BnplProductType[]) =>
			change({ confirmedProducts: types }, { eAct: 'Product Selection', eLab: 'Confirmed' }),
		[change]
	);

	const onConfirmKycData = useCallback(
		() =>
			change(
				{
					singleKycId: application?.singleKycData?.id,
					confirmationState: KYCConfirmationStateEnum.CONFIRMED,
				},
				{ eAct: 'KYC Data', eLab: 'Confirmed' }
			),
		[change, application]
	);

	const onDeclineKycData = useCallback(
		() =>
			change(
				{
					singleKycId: application?.singleKycData?.id,
					confirmationState: KYCConfirmationStateEnum.DECLINED,
				},
				{ eAct: 'KYC Data', eLab: 'Declined' }
			),
		[change, application]
	);

	return {
		loading,
		application,
		isSubmitting,
		error,
		onConfirmProducts,
		onConfirmKycData,
		onDeclineKycData,
	};
}
